import { useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import type { SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import { companyAnalysisQueryKeys } from "app/pages/sales-intelligence/modules/company-analysis/queries/queryKeyFactory";
import BaseSignalsManagementContainer from "app/pages/sales-intelligence/sub-modules/signals/containers/BaseSignalsManagementContainer/BaseSignalsManagementContainer";
import useSignalsFetch from "app/pages/sales-intelligence/sub-modules/signals/hooks/useSignalsFetch";
import {
    isDropdownOpenAction,
    setPreselectedAccountOverviewSignalType,
} from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";
import { fetchSignalsThunk } from "app/pages/sales-intelligence/sub-modules/signals/store/effects";
import {
    selectIsSignalDropdownOpen,
    selectNumberOfSignalsFromSource,
    selectPreselectedAccountOverviewSignalType,
} from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";

const SignalEventManagementContainer = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    useSignalsFetch();

    const isDropdownOpen = useSelector(selectIsSignalDropdownOpen);
    const numberOfSignals = useSelector(selectNumberOfSignalsFromSource);
    const preselectedSignalType = useSelector(selectPreselectedAccountOverviewSignalType);

    const { params } = useCurrentStateAndParamsWithFixedDomain();
    const { key: domain } = params;
    const [isManagementViewOpen, setIsManagementViewOpen] = useState(false);
    const [signalCreateType, setSignalCreateType] = useState<SignalType | null>(null);
    const [shouldUpdateEvents, setShouldUpdateEvents] = useState<boolean>(false);

    function handleClose() {
        dispatch(isDropdownOpenAction(false));
        dispatch(setPreselectedAccountOverviewSignalType(null));
        setSignalCreateType(null);
    }

    function handleUpdateSignalEvents() {
        void queryClient.invalidateQueries({
            queryKey: companyAnalysisQueryKeys.signalEventsBase(domain),
        });

        void dispatch(fetchSignalsThunk());
        setShouldUpdateEvents(false);
    }

    function handleManagementViewClose() {
        if (shouldUpdateEvents) {
            handleUpdateSignalEvents();
        }
        handleClose();
    }

    function handleManagementViewToggle() {
        if (isManagementViewOpen) {
            handleManagementViewClose();
        } else {
            setIsManagementViewOpen(true);
        }
    }

    useEffect(() => {
        setIsManagementViewOpen(isDropdownOpen);
    }, [isDropdownOpen]);

    useEffect(() => {
        if (preselectedSignalType) {
            setSignalCreateType(preselectedSignalType);
        }
    }, [preselectedSignalType]);

    function handleSignalCreate(signalType: SignalType | null) {
        setSignalCreateType(signalType);
    }

    function handleDeleteViewClose() {
        setShouldUpdateEvents(true);
        if (numberOfSignals === 1) {
            handleUpdateSignalEvents();
            dispatch(isDropdownOpenAction(false));
        }
    }

    const handleSignalUpdateSuccess = () => {
        handleUpdateSignalEvents();
        handleClose();
    };

    return (
        <BaseSignalsManagementContainer
            isManagementViewOpen={isManagementViewOpen}
            selectedSignalCreateType={signalCreateType}
            onManagementViewToggle={handleManagementViewToggle}
            onManagementViewClose={handleManagementViewClose}
            onSignalCreate={handleSignalCreate}
            onDeleteViewClose={handleDeleteViewClose}
            onSignalCreateSuccess={handleSignalUpdateSuccess}
            onSignalUpdateSuccess={handleSignalUpdateSuccess}
        />
    );
};

export default SignalEventManagementContainer;
